import React, { useEffect } from 'react';
import {
  ChartCard,
  HorizontalBars,
  InfoTooltip,
  type RelativePerformance,
  getRelativePerformance,
} from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import { useSortedChartData } from '../-hooks/useSortedChartData';
import { OptionalKey } from '../route';

export const KPIImpact_EmissionRemovedFragment = gql(/* GraphQL */ `
  fragment KPIImpact_EmissionRemovedFragment on ForestryIndicators {
    emissionsScope1n2 {
      trimmedAvg
      isDataSuppressed
    }
    emissionsScope1n2_n {
      trimmedAvg
      isDataSuppressed
    }
    emissionsScope1n2_pace {
      trimmedAvg
      isDataSuppressed
    }
  }
`);

type EmissionRemovedProps = {
  className?: string;
  indicators: FragmentType<typeof KPIImpact_EmissionRemovedFragment>;
  viewerIndicators?: FragmentType<typeof KPIImpact_EmissionRemovedFragment>;
  investmentLevel: OptionalKey;
  onEvaluatePerformance: (key: string, performance: RelativePerformance) => void;
};

export function Emissions1And2Removed({
  indicators,
  viewerIndicators,
  investmentLevel,
  className,
  onEvaluatePerformance,
}: EmissionRemovedProps) {
  const benchmarkData = useFragment(KPIImpact_EmissionRemovedFragment, indicators);
  const viewerData = useFragment(KPIImpact_EmissionRemovedFragment, viewerIndicators);

  const benchmarkDataMap = {
    company: benchmarkData.emissionsScope1n2,
    investment: benchmarkData.emissionsScope1n2_n,
    rate_of_change: benchmarkData.emissionsScope1n2_pace,
  };

  const viewerDataMap = {
    company: viewerData?.emissionsScope1n2,
    investment: viewerData?.emissionsScope1n2_n,
    rate_of_change: viewerData?.emissionsScope1n2_pace,
  };

  const selectedBenchmarkData = benchmarkDataMap[investmentLevel];
  const selectedViewerData = viewerDataMap[investmentLevel];

  const { t, title, titleTooltipKPI } = useChartTranslations('emissions1And2Removed');

  const getDisplayValue = (value?: number | null): string => {
    if (value == null) return '';
    const strValue = value.toLocaleString('en', { maximumFractionDigits: 0 });
    return t('displayValue', { value: strValue }) ?? strValue;
  };

  const { sortedChartData } = useSortedChartData({
    selectedBenchmarkData,
    selectedViewerData,
    getDisplayValue,
  });

  const relativePerformance = getRelativePerformance(
    selectedBenchmarkData?.trimmedAvg,
    selectedViewerData?.trimmedAvg
  );

  useEffect(() => {
    onEvaluatePerformance('emissions1And2Removed', relativePerformance);
  }, [onEvaluatePerformance, relativePerformance]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header className="flex items-center justify-between">
        <ChartCard.Title>
          {title}
          {titleTooltipKPI && <InfoTooltip className="ml-1">{titleTooltipKPI}</InfoTooltip>}
        </ChartCard.Title>
        {/* <Button variant="outline" className="shrink-0 font-semibold uppercase">
          view details
        </Button> */}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={selectedBenchmarkData.isDataSuppressed}>
        <HorizontalBars
          min={Math.min(
            0,
            selectedViewerData?.trimmedAvg ?? 0,
            selectedBenchmarkData.trimmedAvg ?? 0
          )}
          max={Math.max(
            0,
            selectedViewerData?.trimmedAvg ?? 0,
            selectedBenchmarkData.trimmedAvg ?? 0
          )}
          showInlineDisplayValue={true}
        >
          {sortedChartData.map((data, index) => (
            <HorizontalBars.Item
              key={index}
              label={data.label}
              value={data.value}
              displayValue={data.displayValue}
              color={data.color}
              isDashed={data.isDashed}
            />
          ))}
        </HorizontalBars>

        {/* <Link href="/impact-kpis/scope-1-and-2-emissions" className="mt-5 lg:hidden">
          <Button variant="outline" size="small" className="shrink-0 font-semibold uppercase">
            view details
          </Button>
        </Link> */}
      </ChartCard.Body>
    </ChartCard>
  );
}
